import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Chip, Skeleton, Stack, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import {
  Apartment,
  ChevronRight,
  Description,
  Groups,
  Insights,
  Spa,
  TrendingUp,
  WorkspacePremium
} from '@mui/icons-material';
import DashboardHero from 'components/dashboard/DashboardHero';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import OrgStatTile from 'components/org-client/OrgStatTile';
import PrivacyNote from 'components/org-client/PrivacyNote';
import useOrgPageTitle from 'components/org-client/useOrgPageTitle';
import routes from 'common/routes';
import useLogger from 'hooks/useLogger';
import {
  MY_ORGANIZATION,
  MY_ORGANIZATION_USAGE,
  MY_ORGANIZATION_WELLBEING
} from 'api/graphql/orgClient';
import heroPhoto from 'images/login.webp';

const WEEKS = 4;

// Accesos del panel: mismas secciones del menú, en el orden en que se leen los datos.
const SHORTCUTS = [
  {
    key: 'wellbeing',
    path: routes.orgClientWellbeing.path,
    icon: <Spa />,
    label: 'Bienestar',
    hint: 'Índice, dimensiones y mapa por departamento'
  },
  {
    key: 'departments',
    path: routes.orgClientDepartments.path,
    icon: <Apartment />,
    label: 'Departamentos',
    hint: 'Cuánto se usa el beneficio en cada área'
  },
  {
    key: 'members',
    path: routes.orgClientMembers.path,
    icon: <Groups />,
    label: 'Colaboradores',
    hint: 'Quiénes tienen el beneficio y con qué plan'
  },
  {
    key: 'reports',
    path: routes.orgClientReports.path,
    icon: <Description />,
    label: 'Reportes',
    hint: 'Histórico y descargas en PDF o Excel'
  },
  {
    key: 'plans',
    path: routes.orgClientPlans.path,
    icon: <WorkspacePremium />,
    label: 'Planes',
    hint: 'Lo habilitado hoy y cómo pedir otro plan'
  }
];

/**
 * Inicio del panel "Prometeo": quién es la empresa (hero con su foto) y los cuatro
 * números que resumen el beneficio. Todo es informativo: desde aquí no se acciona nada sobre
 * las personas, solo se navega al detalle y se descarga.
 */
function OrgClientDashboard() {
  const { t } = useTranslation(['orgClient']);
  const { logError } = useLogger();
  const pageTitle = t('menu.home', { defaultValue: 'Inicio' });
  useOrgPageTitle(pageTitle);

  const { data: orgData, loading: loadingOrg, error: orgError } = useQuery(MY_ORGANIZATION, {
    fetchPolicy: 'cache-and-network',
    onError: (error) => logError('OrgClientDashboard', error)
  });
  const { data: wellbeingData, loading: loadingWellbeing } = useQuery(MY_ORGANIZATION_WELLBEING, {
    variables: { weeks: WEEKS },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: (error) => logError('OrgClientDashboard', error)
  });
  const { data: usageData } = useQuery(MY_ORGANIZATION_USAGE, {
    variables: { weeks: WEEKS },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: (error) => logError('OrgClientDashboard', error)
  });

  const organization = orgData?.myOrganization || null;
  const summary = wellbeingData?.myOrganizationWellbeing || null;
  const usage = useMemo(() => usageData?.myOrganizationUsage || [], [usageData]);

  // Adopción global = personas activas sobre miembros de toda la empresa (no el promedio de
  // los porcentajes por departamento, que le daría el mismo peso a un área de 3 y a una de 80).
  const adoption = useMemo(() => {
    const members = usage.reduce((acc, row) => acc + (row.members || 0), 0);
    const active = usage.reduce((acc, row) => acc + (row.active_members || 0), 0);
    return members > 0 ? Math.round((active / members) * 100) : null;
  }, [usage]);

  const activePlans = (organization?.plan_discounts || []).filter((plan) => plan.is_active);

  if (loadingOrg && !organization) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: 4, mb: 3 }} />
        <Grid container spacing={2.5}>
          {[0, 1, 2, 3].map((index) => (
            <Grid key={index} xs={12} sm={6} lg={3}>
              <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (orgError && !organization) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="error" variant="filled" sx={{ borderRadius: 3 }}>
          {t('errors.organization', {
            defaultValue: 'No pudimos cargar los datos de tu empresa. Intenta de nuevo en un momento.'
          })}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <SEOPageTitle title={pageTitle} />

      <DashboardHero
        backgroundImage={organization?.cover_url || heroPhoto}
        logoImage={organization?.logo_url || null}
        eyebrow={t('brand', { defaultValue: 'Prometeo' })}
        greeting={organization?.name || ''}
        subtitle={t('hero.subtitle', {
          defaultValue: 'El estado del beneficio de bienestar de tu equipo, en agregados anónimos y listo para descargar.'
        })}
        statusChip={
          activePlans.length > 0 ? (
            <Chip
              size="small"
              label={t('hero.plansChip', {
                count: activePlans.length,
                defaultValue: '{{count}} planes habilitados'
              })}
              sx={{
                height: 22,
                borderRadius: '999px',
                fontWeight: 700,
                fontSize: '0.62rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'gold.bright',
                backgroundColor: 'rgba(249, 189, 78, 0.16)'
              }}
            />
          ) : null
        }
      />

      <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
        <Grid xs={12} sm={6} lg={3} data-tour="org-index">
          {loadingWellbeing && !summary ? (
            <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
          ) : (
            <OrgStatTile
              icon={<Insights sx={{ fontSize: 20 }} />}
              label={t('tiles.index', { defaultValue: 'Índice de bienestar' })}
              value={summary?.score ?? '--'}
              unit={summary?.protected ? null : '/ 100'}
              isProtected={Boolean(summary?.protected)}
              hint={t('tiles.indexHint', { weeks: WEEKS, defaultValue: 'Últimas {{weeks}} semanas' })}
            />
          )}
        </Grid>
        <Grid xs={12} sm={6} lg={3}>
          <OrgStatTile
            icon={<TrendingUp sx={{ fontSize: 20 }} />}
            label={t('tiles.participation', { defaultValue: 'Participación' })}
            value={summary ? Math.round(summary.participation_percent) : '--'}
            unit="%"
            hint={t('tiles.participationHint', {
              respondents: summary?.respondents ?? 0,
              members: summary?.members ?? 0,
              defaultValue: '{{respondents}} de {{members}} respondieron el check-in'
            })}
          />
        </Grid>
        <Grid xs={12} sm={6} lg={3}>
          <OrgStatTile
            icon={<Groups sx={{ fontSize: 20 }} />}
            label={t('tiles.members', { defaultValue: 'Colaboradores' })}
            value={organization?.members_count ?? 0}
            hint={t('tiles.membersHint', { defaultValue: 'Con el beneficio activo en tu empresa' })}
          />
        </Grid>
        <Grid xs={12} sm={6} lg={3}>
          <OrgStatTile
            icon={<Spa sx={{ fontSize: 20 }} />}
            label={t('tiles.adoption', { defaultValue: 'Adopción de la app' })}
            value={adoption ?? '--'}
            unit={adoption === null ? null : '%'}
            hint={t('tiles.adoptionHint', { weeks: WEEKS, defaultValue: 'Entrenaron en las últimas {{weeks}} semanas' })}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2.5 }} data-tour="org-privacy">
        <PrivacyNote />
      </Box>

      <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 3.5, mb: 1.5 }}>
        {t('shortcuts.title', { defaultValue: 'Explora tu panel' })}
      </Typography>
      <Grid container spacing={2}>
        {SHORTCUTS.map((shortcut) => (
          <Grid key={shortcut.key} xs={12} sm={6} lg={4}>
            <PremiumCard
              component={RouterLink}
              to={shortcut.path}
              padding={2.25}
              sx={{
                display: 'block',
                textDecoration: 'none',
                color: 'inherit',
                transition: 'transform 200ms cubic-bezier(0.32, 0.72, 0, 1)',
                '&:hover': { transform: 'translateY(-2px)' },
                '&:active': { transform: 'scale(0.98)' }
              }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ display: 'flex', color: 'gold.main' }}>{shortcut.icon}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>
                    {t(`menu.${shortcut.key}`, { defaultValue: shortcut.label })}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {t(`shortcuts.${shortcut.key}`, { defaultValue: shortcut.hint })}
                  </Typography>
                </Box>
                <ChevronRight sx={{ color: 'text.disabled' }} />
              </Stack>
            </PremiumCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default OrgClientDashboard;
